import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Box, createTheme, ThemeProvider } from '@mui/material';
import { BootSpinner } from '@ovComponents/1-primative/bootSpinner/bootSpinner';
import Auth0Provider from '@providers/auth0Provider';
import { useContext } from 'react';
import { LocalizedDatePickerProvider } from './components/fields/localizedDatePicker';
import { AppRouter } from './components/layout/appRouter';
import { AuthProvider } from './providers/AuthProvider';
import { AnalyticsProvider } from './providers/analyticsProvider';
import { ToastContextProvider } from './providers/globalToastProvider';
import OvApolloProvider from './providers/ovApolloProvider';
import ThemeTokenContextProvider, {
  useThemeTokens,
} from './providers/themeTokenProvider';
import UserContextProvider, {
  UserContext,
} from './providers/userContextProvider';
import { useThemeOptions } from './theme';
import 'quill/dist/quill.snow.css';
import 'quill-table-ui/dist/index.css';
import flagsmith from '@flagsmith/flagsmith';
import { FlagsmithProvider } from '@flagsmith/flagsmith/react';
import { CONFIG } from './config';
import InactivityTimeoutProvider from './providers/inActivityTimeoutProvider';

const InternalApp = () => {
  const { loading, userContext } = useContext(UserContext);
  const { sys } = useThemeTokens();
  const theme = createTheme(useThemeOptions());

  if (CONFIG.datadog.env) {
    datadogRum.setUser({
      id: userContext.id,
      organization: userContext.organization?.translatedName?.en,
      role: userContext.role?.translatedName?.en,
      navigationStyle: userContext.role?.navigationStyle,
    });
  }

  return (
    <Box sx={{ backgroundColor: sys.color.background, height: '100vh' }}>
      {loading ? (
        <BootSpinner />
      ) : (
        <ThemeProvider theme={theme}>
          <AppRouter />
        </ThemeProvider>
      )}
    </Box>
  );
};

function App() {
  if (CONFIG.datadog.env) {
    datadogLogs.init({
      clientToken: CONFIG.datadog.logClientToken || '',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: 'onevest-app',
      env: CONFIG.datadog.env,
      version: CONFIG.datadog.ddRumVersion || '',
    });

    datadogRum.init({
      applicationId: CONFIG.datadog.rumAppId || '',
      clientToken: CONFIG.datadog.rumClientToken || '',
      site: 'datadoghq.com',
      service: 'onevest-app',
      version: CONFIG.datadog.ddRumVersion || '',
      env: CONFIG.datadog.env,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
    });
  }

  return (
    <FlagsmithProvider
      options={{
        environmentID: CONFIG.services.flagsmithEnvironmentId,
      }}
      flagsmith={flagsmith}
    >
      <LocalizedDatePickerProvider>
        <ToastContextProvider>
          <OvApolloProvider>
            <AuthProvider>
              <Auth0Provider>
                <UserContextProvider>
                  <AnalyticsProvider>
                    <ThemeTokenContextProvider>
                      <InactivityTimeoutProvider>
                        <InternalApp />
                      </InactivityTimeoutProvider>
                    </ThemeTokenContextProvider>
                  </AnalyticsProvider>
                </UserContextProvider>
              </Auth0Provider>
            </AuthProvider>
          </OvApolloProvider>
        </ToastContextProvider>
      </LocalizedDatePickerProvider>
    </FlagsmithProvider>
  );
}

export default App;
